/* eslint-disable max-lines */
import React from 'react';
import { Prompt } from 'react-router';
import _ from 'lodash';
import { withTheme } from 'styled-components';
import type { Moment } from 'moment';
import moment from 'moment';
import { styled } from '@this/constants/themes';
import { ButtonBase } from '@this/components/shared/atoms/button';
import Text from '@this/components/shared/text/text';

import ContentHeader from '@this/components/shared/content_header/content_header';
import SimpleLoading from '@this/components/shared/simple_loading/simple_loading';

import User, { convertUserResponseToArgs, type UserResponseArgs } from '@this/domain/user/user2';
import NonOrderItem from '@this/domain/non_order_item2';
import NonOrderItemPrice from '@this/domain/non_order_item_price2';
import NonOrderItemPriceDetail from '@this/domain/non_order_item_price_detail2';
import TransportElement from '@this/domain/transport_element2';
import TransportElementTerminal from '@this/domain/transport_element_terminal/transport_element_terminal2';
import HotelElement from '@this/domain/hotel_element2';
import SelectRepository from '@this/domain/select_repository';
import SelectStore from '@this/domain/select_store';
import SearchQuery from '@this/domain/search_query';
import type { AvailableRepository } from '@this/domain/available_repository';
import type { RouteComponentProps } from 'react-router-dom';
import Trip, { convertTripResponseToArgs, type TripResponseArgs } from '@this/src/domain/trip/trip2';
import type TravelerInformation from '@this/domain/traveler_information';
import { buildTravelerParams } from '@this/domain/traveler_information';
import { Fetcher } from '@this/src/util';
import type OrderItem from '@this/domain/order_item2';
import SearchResultItem from '@this/domain/search_result_item';
import { reportError } from '@this/lib/bugsnag';
import { Wrap, BodyWrap, Body, LoadingWrap } from './trips';
import TripsFormBasicinfoTemplate from './trips_form_block/trips_form_basicinfo.template';
import type { AirlineName } from './trips_form_block/trips_form_itemform.template';
import TripsFormItemformTemplate from './trips_form_block/trips_form_itemform.template';
import {
  cloneNonOrderItemForEditing,
  convertTravelerInformationsToTravelerList,
  createInitialTravelerInformations,
  getNonOrderItemElementType
} from './utils';

interface Props extends RouteComponentProps<Record<string, string | undefined>, Record<string, any>, any> {
  serviceId: number;
  availableRepos: AvailableRepository[];
  theme: { themeClass: string };
}

interface TripsDetailState {
  serviceId: number | undefined;
  trip: Trip | null;
  user: User | null;
  members: User[];
  travelers: TravelerInformation[];
  title: string;
  loading: boolean;
  shouldBlockNavigation: boolean;
  validationErrors: TripFormErrors;
  outdate: Moment;
  homedate: Moment;
  query: SearchQuery | undefined;
  store: SelectStore | undefined;
  repository: SelectRepository | undefined;
  domesticAirlines: AirlineName[];
  internationalAirlines: AirlineName[];
  showItemForm: boolean;
  selectedItemType: string | null;
  creatingNonOrderItem: NonOrderItem | null;
  editingNonOrderItem: NonOrderItem | null;
  error: string | null;
  showSearchBox: boolean;
  tripItemFormTemplateErrMsg: string;
  shortdistance: boolean;
  preventZeroAmountApplication: boolean;
}

interface TripsDetailResponse {
  trip: TripResponseArgs | null;
  user: UserResponseArgs;
  members: UserResponseArgs[];
  query: Record<string, unknown> | null;
  shortdistance: boolean;
  prevent_zero_amount_application: boolean;
  show_fee: boolean;
}

interface TripFormErrors {
  travelers: TravelerErrors[];
  hotelDeadline: string | null;
  zeroAmount: string | null;
  errors: string[];
  [key: string]: unknown;
}

interface TravelerErrors {
  memberSelect?: string | null;
  travelerFirstNameRomen?: string | null;
  travelerLastNameRomen?: string | null;
  travelerFirstNameKana?: string | null;
  travelerLastNameKana?: string | null;
  travelerBirthday?: string | null;
  travelerGender?: string | null;
  [key: string]: string | null | undefined;
}

type AddedItemValue = NonOrderItem | OrderItem | SearchResultItem;

const getItemStartDateTime = (item: AddedItemValue) => {
  if (item instanceof NonOrderItem) return item.startDateTime;
  if (item instanceof SearchResultItem) return item.startDateTime();
  if (item.elements && item.elements.length > 0) {
    const element = item.elements[0];
    if ('checkinDate' in element && element.checkinDate) return element.checkinDate;
    if ('startTime' in element && element.startTime) return element.startTime;
  }
  return null;
};

const getIsForeignFlightCustomerInfoRequired = (trip: Trip | null, store: SelectStore | undefined) => {
  if (trip?.foreignAir) return true;
  const resultItems = store?.result.items || [];
  return resultItems.some(item => item.elementType() === 'flight');
};

const getNonOrderItemParams = (item: NonOrderItem) => item.updateParams;

const buildNonOrderItem = (elementType: string, peopleNum: number, outdate: Moment, homedate: Moment) => {
  const timeZone = 'Asia/Tokyo';
  const from = new TransportElementTerminal({
    time: outdate.clone().startOf('day'),
    timeZone,
    name: ''
  });
  const to = new TransportElementTerminal({
    time: homedate.clone().startOf('day'),
    timeZone,
    name: ''
  });
  const details =
    elementType === 'rental_car'
      ? [new NonOrderItemPriceDetail({ id: 0, price: 0, purpose: 'rental_car_gas' })]
      : [];
  const price = new NonOrderItemPrice({ id: 0, price: 0, details });

  if (elementType === 'hotel') {
    const hotel = new HotelElement({
      // idをつけて初期化しているが送信しないようにしている
      id: 0,
      name: '',
      roomNum: 1,
      checkinDate: outdate.clone().startOf('day'),
      checkoutDate: homedate.clone().startOf('day'),
      provider: '',
      planName: '',
      roomType: '',
      restricted: false,
      tooLate: false
    });
    return new NonOrderItem({
      price,
      peopleNum,
      nonOrderItemMappings: [],
      transports: [],
      hotels: [hotel],
      paymentType: 'cash',
      requestType: 'simple_request',
      isNeed: true,
      supplier: '',
      tripType: 'round_trip',
      elementType
    });
  }

  const transport = new TransportElement({
    transportType: elementType as never,
    from,
    to,
    boardingStation: '',
    arrivalStation: ''
  });
  return new NonOrderItem({
    price,
    peopleNum,
    nonOrderItemMappings: [],
    transports: [transport],
    hotels: [],
    paymentType: 'cash',
    requestType: 'simple_request',
    isNeed: true,
    supplier: '',
    tripType: 'round_trip',
    elementType
  });
};

const isTripEditable = (trip: Trip | null) => {
  if (!trip) return true;
  const draft = Boolean(trip.draft);
  return draft || trip.tripDetailEditable;
};

class TripsDetailForm extends React.Component<Props, TripsDetailState> {
  constructor(props: Props) {
    super(props);
    const lState = props.location.state;

    this.state = {
      serviceId: props.serviceId,
      trip: null,
      user: null,
      members: [],
      travelers: [],
      title: '',
      loading: true,
      shouldBlockNavigation: true,
      validationErrors: {
        travelers: [],
        hotelDeadline: null,
        zeroAmount: null,
        errors: []
      },
      outdate: lState && lState.outdate ? moment(lState.outdate) : moment().add(1, 'days'),
      homedate: lState && lState.homedate ? moment(lState.homedate) : moment().add(2, 'days'),
      query: undefined,
      store: undefined,
      repository: undefined,
      domesticAirlines: [],
      internationalAirlines: [],
      showItemForm: true,
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null,
      error: null,
      showSearchBox: false,
      tripItemFormTemplateErrMsg: '',
      shortdistance: false,
      preventZeroAmountApplication: false
    };
  }

  componentDidMount() {
    const lState = this.props.location.state;
    Fetcher.get<TripsDetailResponse>(`/trips/${this.props.match.params.id}/detail.json`, lState || {}).then(
      result => {
        const user = new User(convertUserResponseToArgs(result.user));
        const members = _.map(result.members, m => new User(convertUserResponseToArgs(m)));
        const trip = result.trip
          ? new Trip(convertTripResponseToArgs({ ...result.trip, user: result.user }, result.show_fee ?? false))
          : null;
        const travelerCount = trip?.peoplenum || 1;
        const baseTravelers =
          trip && trip.travelerInformations.length > 0
            ? trip.travelerInformations
            : createInitialTravelerInformations(travelerCount, user, members);
        const travelers = baseTravelers.map(traveler => {
          if (traveler.type === 'self' && !traveler.user) {
            traveler.user = user;
            traveler.assignFromUser(user);
          }
          if (traveler.type === 'member' && traveler.userId && !traveler.user) {
            const member = members.find(m => m.id === traveler.userId);
            if (member) {
              traveler.user = member;
              traveler.assignFromUser(member);
            }
          }
          return traveler;
        });
        const travelerList = convertTravelerInformationsToTravelerList(travelers, travelers.length, user, members);
        const queryStartTime = utils.dig(result.query, 'departure_time')
          ? moment(utils.dig(result.query, 'departure_time'))
          : trip?.startTime || this.state.outdate;
        const queryEndTime = utils.dig(result.query, 'return_time')
          ? moment(utils.dig(result.query, 'return_time'))
          : trip?.endTime || this.state.homedate;
        const query = result.query
          ? new SearchQuery(
              _.merge({}, result.query, {
                cabin: (user.defaultSeatClasses || []).join(','),
                defaultSeatClasses: user.defaultSeatClasses,
                travelers: travelerList,
                time: {
                  startTime: queryStartTime,
                  endTime: queryEndTime
                },
                availableRepos: this.props.availableRepos
              })
            )
          : new SearchQuery({
              cabin: (user.defaultSeatClasses || []).join(','),
              defaultSeatClasses: user.defaultSeatClasses,
              peoplenum: travelers.length,
              travelers: travelerList,
              time: {
                startTime: trip?.startTime || this.state.outdate,
                endTime: trip?.endTime || this.state.homedate
              },
              availableRepos: this.props.availableRepos
            });
        const tripStartTime = trip?.cacheStartTime || trip?.startTime;
        const tripEndTime = trip?.cacheEndTime || trip?.endTime;
        const outdate = tripStartTime ? moment(tripStartTime) : this.state.outdate;
        const homedate = tripEndTime ? moment(tripEndTime) : this.state.homedate;
        this.fetchAirlines();

        this.setState(
          {
            trip,
            user,
            members,
            travelers,
            title: trip !== null && trip.id !== null ? trip.title || this.state.title : this.state.title,
            outdate,
            homedate,
            query,
            loading: false,
            shortdistance: result.shortdistance,
            preventZeroAmountApplication: result.prevent_zero_amount_application
          },
          () => {
            if (trip?.draft && result.query && query) {
              this.handleSmartSearch(query);
            }
          }
        );
      },
      () => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        });
      }
    );
  }

  componentDidUpdate() {
    // 未保存離脱の警告をPromptで実装しているが、再読み込みとタブの終了に対応していないため
    if (this.state.shouldBlockNavigation) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  }

  async fetchAirlines() {
    const domestic = await Fetcher.get<{ airlines: AirlineName[] }>('/domestic_airlines.json');
    const international = await Fetcher.get<{ airlines: AirlineName[] }>('/international_airlines.json');
    this.setState({
      domesticAirlines: domestic.airlines,
      internationalAirlines: international.airlines
    });
  }

  addedItems = () => {
    if (!this.state.trip) return [];
    let items: AddedItemValue[] = [...(this.state.trip.nonOrderItems as NonOrderItem[])];
    if (this.state.trip.draft) {
      items = items.concat(this.state.store?.result.items || []);
    } else {
      items = items.concat(this.state.trip.currentOrder?.orderItems || []);
    }

    return _.sortBy(items, item => {
      const startDateTime = getItemStartDateTime(item);
      return startDateTime ? startDateTime.valueOf() : 0;
    });
  };

  handleShowItemForm = () => {
    this.setState({
      showItemForm: true,
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null
    });
  };

  handleSmartSearch = (query: SearchQuery) => {
    Fetcher.post('/search_histories', query.searchHistoryParams());
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        if (this.state.query) {
          this.props.history.push(`${location.pathname}${query.getQueryString()}`);

          const store = new SelectStore({ query: this.state.query }, this.state.user, this.props.availableRepos);

          this.setState(
            {
              store,
              repository: new SelectRepository({ store })
            },
            () => {
              if (this.state.repository) {
                this.state.repository.startSearch(this.props.theme.themeClass);
              }
            }
          );
        }

        this.setState({
          shouldBlockNavigation: true
        });
      }
    );
  };

  selectItemType = (elementType: string) => {
    if (this.state.trip) {
      const item = buildNonOrderItem(
        elementType,
        this.state.travelers.length,
        this.state.outdate,
        this.state.homedate
      );
      this.setState({
        selectedItemType: elementType,
        creatingNonOrderItem: item
      });
    }
  };

  deselectItemType = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    this.setState({
      selectedItemType: null,
      creatingNonOrderItem: null,
      editingNonOrderItem: null
    });
  };

  addNonOrderItemToTrip = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!this.state.creatingNonOrderItem) {
      return;
    }
    const trip = this.state.trip;
    if (!trip) return;

    const totalPrice =
      this.state.creatingNonOrderItem.price.totalPrice * this.state.creatingNonOrderItem.peopleNum;
    if (isNaN(totalPrice)) {
      this.setState({ tripItemFormTemplateErrMsg: '金額を入力してください' });
      return;
    }
    if (totalPrice === 0 && this.state.preventZeroAmountApplication) {
      this.setState({ tripItemFormTemplateErrMsg: '金額は1円以上で入力してください' });
      return;
    }

    if (this.state.editingNonOrderItem) {
      const nonOrderItems = trip.nonOrderItems as NonOrderItem[];
      trip.nonOrderItems = _.filter(
        nonOrderItems,
        item => item !== this.state.editingNonOrderItem
      ) as NonOrderItem[];
    }
    (trip.nonOrderItems as NonOrderItem[]).push(this.state.creatingNonOrderItem);
    this.setState({
      trip,
      showItemForm: false,
      selectedItemType: null,
      creatingNonOrderItem: null,
      tripItemFormTemplateErrMsg: ''
    });
  };

  static handleTimeChange(item: NonOrderItem, type: 'from' | 'to', time: Moment) {
    item.transports[0][type].time = time;
    if (item.transports[0].from.time > item.transports[0].to.time) {
      item.transports[0][type === 'from' ? 'to' : 'from'].time = item.transports[0][type].time;
    }
    app.render();
  }

  startEditingNonOrderItem = (item: NonOrderItem) => {
    this.setState({
      showItemForm: true,
      selectedItemType: getNonOrderItemElementType(item),
      creatingNonOrderItem: cloneNonOrderItemForEditing(item),
      editingNonOrderItem: item
    });
  };

  toggleNonOrderItemNeed = (item: NonOrderItem) => {
    item.isNeed = !item.isNeed;
    app.render();
    if (this.state.editingNonOrderItem === item) {
      this.setState({
        selectedItemType: null,
        creatingNonOrderItem: null,
        editingNonOrderItem: null
      });
    }
  };

  handleSelectItem = () => {
    this.updateUrl();
  };

  private updateUrl() {
    this.setState(
      {
        shouldBlockNavigation: false
      },
      () => {
        if (this.state.store && this.state.query) {
          let href = location.pathname;
          href += this.state.query.getQueryString();
          if (this.state.store.result) {
            href += this.state.store.result.getQueryString();
          }
          this.props.history.push(href);
          const params = utils.getParams();
          _.map(params.items, (item, i) => {
            if (item.item_type === 'hotel') {
              _.merge(item, {
                prefecture: this.state.repository ? this.state.repository.getPrefecture(Number(i)) : ''
              });
            }
            return item;
          });
        }

        this.setState({
          shouldBlockNavigation: true
        });
      }
    );
  }

  handleSubmit = (e: React.MouseEvent<HTMLElement>) => {
    const validationErrors = this.validationErrors();
    if (_.isEmpty(validationErrors)) {
      this.submit();
    } else {
      e.preventDefault();
      this.setState({ validationErrors });
    }
  };

  submit() {
    if (!this.state.trip) return;
    Fetcher.put(`/trips/${this.state.trip.id}/detail`, this.submitParams()).then(
      () => {
        this.setState(
          {
            shouldBlockNavigation: false
          },
          () => {
            this.props.history.push('/trips');
          }
        );
      },
      () => {
        this.setState({
          error: '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        });
      }
    );
  }

  submitParams() {
    const isForeignFlightCustomerInfoRequired = getIsForeignFlightCustomerInfoRequired(
      this.state.trip,
      this.state.store
    );
    return {
      params: {
        title: this.state.title,
        travelers: buildTravelerParams(this.state.travelers, isForeignFlightCustomerInfoRequired),
        departure_time: this.state.outdate.format('YYYY-MM-DD HH:mm ZZ'),
        return_time: this.state.homedate.format('YYYY-MM-DD HH:mm ZZ'),
        non_order_items: _.compact(
          _.map(this.state.trip?.nonOrderItems || [], item => getNonOrderItemParams(item))
        ),
        search_result: this.state.store ? this.state.store.result.confirmParams() : {}
      },
      query: this.state.store && this.state.query ? this.state.query.getSubmitParams() : {}
    };
  }

  validZeroAmount = () => {
    if (!this.state.preventZeroAmountApplication) {
      return true;
    }
    const nonOrderItems: NonOrderItem[] = this.state.trip?.nonOrderItems || [];
    const neededNonOrderItems = nonOrderItems.filter(item => item.isNeed);

    const orderItems: OrderItem[] = this.state.trip?.currentOrder?.orderItems || [];
    return neededNonOrderItems.length > 0 || orderItems.length > 0;
  };

  validationErrors() {
    const errors: TripFormErrors = {
      travelers: [],
      hotelDeadline: null,
      zeroAmount: null,
      errors: []
    };
    errors.zeroAmount = this.validZeroAmount()
      ? null
      : '0円での申請は設定で禁止されています。旅程内容と金額を1つ以上入力してください。';
    return utils.compactObject(errors);
  }

  toggleShowSearchBox = () => {
    this.setState({ showSearchBox: !this.state.showSearchBox });
  };

  handleOpenShowSearchBox = () => {
    this.setState({ showSearchBox: true });
  };

  changeSmartSearch = () => {};

  handleTravelerSelect = (i: number, traveler: TravelerInformation) => {
    this.setState(
      prev => {
        const travelers = [...prev.travelers];
        travelers[i] = traveler;
        return { travelers } as Pick<TripsDetailState, 'travelers'>;
      },
      () => this.setTripTravelersToQuery()
    );
  };

  setTripTravelersToQuery = () => {
    if (!this.state.query || !this.state.user) {
      return;
    }

    const travelerList = convertTravelerInformationsToTravelerList(
      this.state.travelers,
      this.state.travelers.length,
      this.state.user,
      this.state.members
    );
    const query = this.state.query;
    query.travelers = travelerList;
    this.setState({ query });
  };

  static requiredError(name: string, value: string, message = 'を入力してください') {
    if (!value || value.length === 0) {
      return name + message;
    }
    return null;
  }

  static katakanaError(name: string, value: string) {
    const error = TripsDetailForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value.match(/^[ァ-ヶー　]*$/)) {
      return `${name}はカタカナで入力してください`;
    }
    return null;
  }

  static alphabetError(name: string, value: string) {
    const error = TripsDetailForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!value.match(/^[a-zA-Z]+$/)) {
      return `${name}はアルファベッドで入力してください`;
    }
    return null;
  }

  static dateError(name: string, value: string) {
    const error = TripsDetailForm.requiredError(name, value);
    if (error) {
      return error;
    }
    if (!moment(value).isValid()) {
      return `${name}を正しく入力してください（例：1986-01-01）`;
    }
    return null;
  }

  render() {
    const {
      serviceId,
      trip,
      title,
      user,
      members,
      travelers,
      loading,
      shouldBlockNavigation,
      validationErrors,
      outdate,
      homedate,
      query,
      store,
      repository,
      domesticAirlines,
      internationalAirlines,
      showItemForm,
      selectedItemType,
      creatingNonOrderItem,
      editingNonOrderItem,
      error,
      showSearchBox,
      tripItemFormTemplateErrMsg,
      shortdistance
    } = this.state;
    try {
      const editableTrip = isTripEditable(trip);
      return (
        <Wrap>
          <Prompt
            when={shouldBlockNavigation}
            message="保存されていないデータが失われます。本当によろしいですか？"
          />
          <FormBodyWrap>
            <div>
              <FormBody>
                <ContentHeader title="旅程情報詳細" backButton backLink="/trips" />
                <TripForm>
                  <TripFormLeft>
                    {loading ? (
                      <LoadingWrap>
                        <SimpleLoading />
                      </LoadingWrap>
                    ) : error ? (
                      <LoadingWrap>
                        <Error>{error}</Error>
                      </LoadingWrap>
                    ) : user ? (
                      <TripsFormBasicinfoTemplate
                        editable={false}
                        travelers={travelers}
                        user={user}
                        title={title}
                        members={members}
                        outdate={outdate}
                        homedate={homedate}
                        handleTravelerSelect={this.handleTravelerSelect}
                        validationErrors={validationErrors}
                      />
                    ) : (
                      <LoadingWrap>
                        <Error>通信環境が不安定です。時間をおいてもう一度お試しください。</Error>
                      </LoadingWrap>
                    )}
                  </TripFormLeft>
                  <TripFormRight>
                    <Section>
                      <SectionIn>
                        {editableTrip && <SubmitButton onClick={e => this.handleSubmit(e)}>保存</SubmitButton>}
                        {!_.isEmpty(validationErrors.hotelDeadline) && (
                          <Error>
                            <Text text={validationErrors.hotelDeadline} />
                          </Error>
                        )}
                        {!_.isEmpty(validationErrors.zeroAmount) && (
                          <Error>
                            <Text text={validationErrors.zeroAmount} />
                          </Error>
                        )}
                        {!_.isEmpty(validationErrors.errors) && (
                          <Error>
                            <Text text={validationErrors.errors.join('\n')} />
                          </Error>
                        )}
                      </SectionIn>
                    </Section>
                  </TripFormRight>
                </TripForm>
                <TripsFormItemformTemplate
                  serviceId={serviceId}
                  user={user}
                  trip={trip}
                  query={query}
                  store={store}
                  repository={repository}
                  domesticAirlines={domesticAirlines}
                  internationalAirlines={internationalAirlines}
                  addedItems={this.addedItems()}
                  editingNonOrderItem={editingNonOrderItem}
                  creatingNonOrderItem={creatingNonOrderItem}
                  selectedItemType={selectedItemType}
                  selectItemType={this.selectItemType}
                  deselectItemType={this.deselectItemType}
                  startEditingNonOrderItem={this.startEditingNonOrderItem}
                  toggleNonOrderItemNeed={this.toggleNonOrderItemNeed}
                  handleSelectItem={this.handleSelectItem}
                  handleOpenShowSearchBox={this.handleOpenShowSearchBox}
                  handleShowItemForm={this.handleShowItemForm}
                  toggleShowSearchBox={this.toggleShowSearchBox}
                  changeSmartSearch={this.changeSmartSearch}
                  handleSmartSearch={this.handleSmartSearch}
                  addNonOrderItemToTrip={this.addNonOrderItemToTrip}
                  handleTimeChange={TripsDetailForm.handleTimeChange}
                  tripItemFormTemplateErrMsg={tripItemFormTemplateErrMsg}
                  isSmartSearching={false}
                  showItemForm={showItemForm}
                  showSearchBox={showSearchBox}
                  shortdistance={shortdistance}
                  useSmartSearch={false}
                />
              </FormBody>
            </div>
            <FooterActionGroup>
              <FooterButtonArea>
                {editableTrip && (
                  <SubmitButton onClick={e => this.handleSubmit(e)}>
                    {trip === null || trip.draft ? '次へ' : '保存'}
                  </SubmitButton>
                )}
              </FooterButtonArea>
            </FooterActionGroup>
          </FormBodyWrap>
        </Wrap>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const FormBodyWrap = styled(BodyWrap)`
  padding: 20px 0;
`;

const FormBody = styled(Body)`
  padding: 0;
`;

const TripForm = styled.div`
  display: flex;
  padding: 20px;
`;

const TripFormLeft = styled.div`
  width: 80%;
`;

const TripFormRight = styled.div`
  margin: 0 0 0 auto;
`;

const Section = styled.div`
  padding-bottom: 20px;
`;

const SectionIn = styled.div`
  margin-left: 10px;
  font-size: 13px;
`;

const Error = styled.div`
  color: ${props => props.theme.redColor};
`;

const SubmitButton = styled.button`
  ${ButtonBase};
  font-size: 13px;
  width: 200px;
`;

const FooterActionGroup = styled.div`
  background: #fff;
  position: sticky;
  bottom: 0;
  box-shadow: 0 -3px 3px 3px rgba(0, 0, 0, 0.1);
`;

const FooterButtonArea = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
`;

export default withTheme(TripsDetailForm);
